import { Grid, Paper, TablePagination } from '@mui/material';
import DashboardLayout from '@examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from '@examples/Navbars/DashboardNavbar';
import MDButton from '@components/MDButton';
import { useMaterialUIController } from 'context';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PersonService } from './services/person.service';
import { PersonResponse } from './services/responses/person.response';
import { PersonHook } from './hooks/person.hook';
import { PeopleFilter } from './components/people.filter';
import { PeopleTable } from './components/people.table';
import { PeopleModal } from './components/people.modal';

export const People = () => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const { sidenavColor } = controller;
    const navigate = useNavigate();
    const service = new PersonService();

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchParams] = useSearchParams();
    const searchFromUrl = searchParams.get('busca') ?? '';
    const [search, setSearch] = useState(searchFromUrl);
    const [filterFinal, setFilterFinal] = useState(
        searchFromUrl ? `?search=${encodeURIComponent(searchFromUrl)}` : ''
    );
    const [orderBy, setOrderBy] = useState('Name');
    const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('asc');
    const [refresh, setRefresh] = useState(true);
    const [total, setTotal] = useState(0);
    const [openModal, setOpenModal] = useState(false);
    const [editItem, setEditItem] = useState<PersonResponse | null>(null);

    const { json } = new PersonHook({
        page,
        limit: rowsPerPage,
        orderBy,
        orderDirection,
        filter: filterFinal,
        refresh,
        service,
    }).getJson();

    useEffect(() => {
        setTotal(json?.data?.totalItens ?? 0);
    }, [json]);

    const applySearch = () => {
        setPage(0);
        setFilterFinal(search ? `?search=${encodeURIComponent(search)}` : '');
    };

    const clear = () => {
        setSearch('');
        setFilterFinal('');
        setRefresh((p) => !p);
    };

    /// Clicar na coluna que já ordena inverte o sentido; em outra, começa de A a Z.
    const changeOrder = (field: string) => {
        if (field === orderBy) {
            setOrderDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
            return;
        }

        setOrderBy(field);
        setOrderDirection('asc');
    };

    const getAll = () => setRefresh((p) => !p);

    const handleModal = () => {
        if (openModal) setEditItem(null);
        setOpenModal((p) => !p);
    };

    const getById = async (id: string) => {
        const res = await service.getById(id);
        setEditItem(res.data.data);
        setOpenModal(true);
    };

    return (
        <DashboardLayout>
            <DashboardNavbar title={t('people.titulo')} />

            <Grid container spacing={1}>
                <PeopleFilter
                    search={search}
                    setSearch={setSearch}
                    onSearch={applySearch}
                    onClear={clear}
                    onEnter={(e) => e.key === 'Enter' && applySearch()}
                />
                <Grid size={{ xs: 12, md: 2 }} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
                    <MDButton variant="gradient" color="dark" onClick={() => navigate('/crm/pessoas/importar')} sx={{ height: 40, minWidth: 110, whiteSpace: 'nowrap' }}>
                        {t('people.importar.botao')}
                    </MDButton>

                    <MDButton variant="gradient" color={sidenavColor} onClick={handleModal} sx={{ height: 40, minWidth: 130, whiteSpace: 'nowrap' }}>
                        {t('general.adicionar')}
                    </MDButton>
                </Grid>
            </Grid>

            <Paper sx={{ width: '100%', overflow: 'hidden', mt: 2 }}>
                <PeopleTable
                    data={json}
                    editAction={getById}
                    refresh={refresh}
                    setRefresh={setRefresh}
                    orderBy={orderBy}
                    orderDirection={orderDirection}
                    onOrder={changeOrder}
                    searching={filterFinal !== ''}
                />
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    labelRowsPerPage={t('general.linhasPorPagina')}
                    labelDisplayedRows={({ from, to, count }) =>
                        `${from}-${to} ${t('general.X-de-X')} ${count !== -1 ? count : `${t('general.maisQue')} ${to}`}`
                    }
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value));
                        setPage(0);
                    }}
                />
            </Paper>

            <PeopleModal
                open={openModal}
                edit={editItem}
                handleModal={handleModal}
                getAll={getAll}
            />
        </DashboardLayout>
    );
};
